import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { getLocalSectionData } from 'src/app/pages/homepage/shared/api';
import { useLocale } from 'src/app/shared/hooks/l10n';
import { useMappedLoadedState } from 'src/app/shared/hooks/state';
import type { State } from 'src/app/shared/utils/async';

import { useUserLocation } from './useUserLocation';

export type LocalData = Awaited<ReturnType<typeof getLocalSectionData>>;
type Result = ModelHookResult<
	{
		locationData: State<{ value: LocalData }>;
		isModalOpen: boolean;
	},
	{
		onLocalSectionClick: (location: NonNullable<LocalData>) => void;
		handleEditLocationClick: (location: NonNullable<LocalData>) => void;
		handleCloseModal: () => void;
	}
>;

export function useLocalSection(): Result {
	const userLocation = useUserLocation();
	const utilityContext = useUtilityContext();
	const track = useTracking();
	const locale = useLocale();

	const [isModalOpen, setIsModalOpen] = useState(false);

	const queryState = useQuery({
		// NOTE: Fetches the initial location for local section.
		// This implementation currently only supports the 'us' country and 'en-us' locale
		// The edit local section feature is built specifically for the current U.S. configuration
		// Slug creation and query logic are tied to 'us' and 'en-us' values
		// Extending this to support additional countries or locales will require:
		//     - Introducing a localization strategy for country/locale mappings
		//     - Refactoring the slug generation and query logic to be dynamic
		// TODO: Support additional countries/locales beyond 'us' and 'en-us'
		//       See ticket: https://change.atlassian.net/jira/software/c/projects/CHANGE/boards/375?selectedIssue=CHANGE-74243
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: [getLocalSectionData.queryKey],
		queryFn: async () => getLocalSectionData(userLocation, locale, utilityContext),
	});
	const locationData = useMappedLoadedState(queryState, ({ data }) => ({ value: data }));

	const handleEditLocationClick = (location: NonNullable<LocalData>) => {
		void track('homepage_local_edit_location_click', {
			location: location.shortName,
			slug: location.slug,
		});

		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	return {
		data: { locationData, isModalOpen },
		actions: {
			onLocalSectionClick: (location: NonNullable<LocalData>) => {
				void track('homepage_local_page_click', {
					location: location.shortName,
					slug: location.slug,
				});
			},
			handleEditLocationClick,
			handleCloseModal,
		},
	};
}
